/**
 * Resilient WebSocket client for the copilot service.
 *
 * Connects to `${COPILOT_WS_URL}/ws/copilot/<conversationUuid>/`, passing the
 * doctor's raw DRF token via the `Sec-WebSocket-Protocol` subprotocol
 * (`token.<raw>`, alongside `copilot.v1`). The copilot's TokenAuthMiddleware
 * verifies it against pacs-be. Auto-reconnects with exponential backoff and
 * replays buffered frames via `resume` using the last acked seq.
 */

import {
  buildEnvelope,
  Envelope,
  ResumePayload,
  UserMessagePayload,
  VoiceChunkPayload,
  WS_SUBPROTOCOL,
} from './protocol';

export type WsStatus = 'connecting' | 'open' | 'closed' | 'error';

interface CopilotWsOptions {
  baseUrl: string;
  conversationUuid: string;
  token: string;
  onMessage: (env: Envelope) => void;
  onStatus?: (status: WsStatus) => void;
}

const PING_INTERVAL_MS = 25_000;
const BACKOFF_BASE_MS = 500;
const BACKOFF_CAP_MS = 15_000;

export class CopilotWsClient {
  private ws: WebSocket | null = null;

  private readonly opts: CopilotWsOptions;

  private lastAckSeq = 0;

  private attempt = 0;

  private pingTimer: ReturnType<typeof setInterval> | null = null;

  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  private closedByUser = false;

  constructor(opts: CopilotWsOptions) {
    this.opts = opts;
  }

  connect(): void {
    // Drop any existing socket first so repeated connect() calls don't leak a
    // background socket that keeps firing callbacks / reconnecting.
    this.teardown();
    this.closedByUser = false;
    this.openSocket();
  }

  private openSocket(): void {
    const { baseUrl, conversationUuid, token } = this.opts;
    const url = `${baseUrl.replace(/\/$/, '')}/ws/copilot/${conversationUuid}/`;
    this.setStatus('connecting');
    // Browsers can't set headers on the upgrade, so the token rides the
    // subprotocol list. Never log this array — it carries the auth token.
    const ws = new WebSocket(url, [`token.${token}`, WS_SUBPROTOCOL]);
    this.ws = ws;

    ws.onopen = () => {
      this.attempt = 0;
      this.setStatus('open');
      this.startPing();
      if (this.lastAckSeq > 0) {
        this.send('resume', { last_ack_seq: this.lastAckSeq } as ResumePayload);
      }
    };

    ws.onmessage = (event) => {
      let env: Envelope;
      try {
        env = JSON.parse(event.data as string) as Envelope;
      } catch {
        return;
      }
      if (typeof env.seq === 'number' && env.seq > this.lastAckSeq) {
        this.lastAckSeq = env.seq;
      }
      this.opts.onMessage(env);
    };

    ws.onerror = () => this.setStatus('error');

    ws.onclose = () => {
      this.stopPing();
      this.setStatus('closed');
      if (!this.closedByUser) this.scheduleReconnect();
    };
  }

  private scheduleReconnect(): void {
    const delay = Math.min(BACKOFF_BASE_MS * 2 ** this.attempt, BACKOFF_CAP_MS);
    this.attempt += 1;
    this.reconnectTimer = setTimeout(() => this.openSocket(), delay);
  }

  sendUserMessage(text: string, studyContext?: Record<string, unknown>): void {
    this.send('user_message', {
      text,
      study_context: studyContext,
    } as UserMessagePayload);
  }

  /** Stream one base64 LINEAR16 PCM chunk for dictation. */
  sendVoiceChunk(audioB64: string): void {
    this.send('voice_chunk', { audio_b64: audioB64 } as VoiceChunkPayload);
  }

  /** Signal end-of-utterance so the server finalizes the transcript. */
  endVoice(): void {
    this.send('end_voice', {});
  }

  private send(
    type:
      | 'user_message'
      | 'ping'
      | 'resume'
      | 'ack'
      | 'voice_chunk'
      | 'end_voice',
    payload: unknown
  ): void {
    if (this.ws?.readyState !== WebSocket.OPEN) return;
    this.ws.send(JSON.stringify(buildEnvelope(type, payload)));
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = setInterval(() => this.send('ping', {}), PING_INTERVAL_MS);
  }

  private stopPing(): void {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  private setStatus(status: WsStatus): void {
    this.opts.onStatus?.(status);
  }

  close(): void {
    this.closedByUser = true;
    this.teardown();
  }

  /** Close the current socket + timers and detach handlers so the closing
   * socket can't schedule a reconnect or deliver stray messages. */
  private teardown(): void {
    this.stopPing();
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      this.ws.onopen = null;
      this.ws.onmessage = null;
      this.ws.onerror = null;
      this.ws.onclose = null;
      this.ws.close();
      this.ws = null;
    }
  }
}
